import { useEffect, useMemo, useState } from 'react'
import { resetPassword } from 'wasp/client/auth'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '../../v32/components/ui/card'
import { Input } from '../../v32/components/ui/input'
import { Label } from '../../v32/components/ui/label'
import { Button } from '../../v32/components/ui/button'
import { track as trackAnalytics } from '../../analytics'

export function PasswordResetPage(): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search])
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    try { trackAnalytics('auth_view', { page: 'password_reset' }) } catch {}
  }, [])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!token) { setError('Invalid or missing token.'); return }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (password !== confirm) { setError('Passwords do not match.'); return }
    setSubmitting(true)
    try {
      const res = await resetPassword({ token, password })
      if (res?.success) {
        setDone(true)
        try { trackAnalytics('auth_submit', { page: 'password_reset', success: true }) } catch {}
        setTimeout(() => navigate('/login'), 1200)
      } else {
        setError('Unable to reset password. The link may be invalid or expired.')
      }
    } catch (err: any) {
      setError(err?.message || 'Unable to reset password')
      try { trackAnalytics('auth_submit', { page: 'password_reset', success: false }) } catch {}
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-sand flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md border-0 shadow-lg rounded-lg">
          <CardHeader className="text-center pb-6">
            <div className="text-3xl font-bold text-dusk mb-2">nura</div>
            <h1 className="text-2xl font-semibold text-dusk mb-2">Set a new password</h1>
            <p className="text-muted-foreground">Please enter your new password below.</p>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-dusk">Password updated successfully.</p>
                <div className="flex items-center justify-center gap-3">
                  <Link to="/login" className="text-warning-600 hover:text-warning-700 text-sm font-medium">Go to sign in</Link>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                {!token && (
                  <p className="text-sm text-red-600">Missing reset token. Use the link from your email.</p>
                )}
                <div>
                  <Label htmlFor="password">New password</Label>
                  <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm password</Label>
                  <Input id="confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <Button type="submit" className="w-full" disabled={submitting || !token}>
                  {submitting ? 'Updating…' : 'Update password'}
                </Button>
                <div className="text-center text-sm">
                  <span className="text-muted-foreground">Back to </span>
                  <Link to="/login" className="text-warning-600 hover:text-warning-700 font-medium">sign in</Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
